import { z } from 'zod';
import isNil from 'lodash/isNil';
import { MAX_INTEGER } from '../../../../../config/constants';
import { RelationalOperatorKey } from '../../../../../utils/apollo/graphqlSchema';

const MIN_VALUE = 0;
const MAX_VALUE = MAX_INTEGER;
const PRECISION = 4;

const numberSchema = z.coerce
  .number()
  .gte(MIN_VALUE)
  .lte(MAX_VALUE)
  .superRefine((value, ctx) => {
    if (!value?.toString()?.includes('.')) {
      return;
    }

    const valid = value.toString().split('.')[1]?.length <= PRECISION;

    if (!valid) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Up to ${PRECISION} decimal places allowed`,
        fatal: true,
      });

      return z.NEVER;
    }
  });

export const singleMeasureFilterSchema = z
  .object({
    measureKey: z.string(),
    relOpKey: z.nativeEnum(RelationalOperatorKey).nullable(),
    fromValue: numberSchema.nullable(),
    toValue: numberSchema.nullable(),
    value: numberSchema.nullable(),
  })
  .superRefine((values, ctx) => {
    const { relOpKey, fromValue, toValue, value } = values;

    if (isNil(relOpKey)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Value is required',
        path: ['relOpKey'],
        fatal: true,
      });

      return z.NEVER;
    }

    if (relOpKey === RelationalOperatorKey.Between) {
      if (isNil(fromValue)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Value is required',
          path: ['fromValue'],
          fatal: true,
        });

        return z.NEVER;
      }

      if (isNil(toValue)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Value is required',
          path: ['toValue'],
          fatal: true,
        });

        return z.NEVER;
      }

      if (fromValue >= toValue) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Should be less than ${toValue}`,
          path: ['fromValue'],
          fatal: true,
        });

        return z.NEVER;
      }
    } else {
      if (isNil(value)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Value is required',
          path: ['value'],
          fatal: true,
        });

        return z.NEVER;
      }

      if (relOpKey === RelationalOperatorKey.Lessthan && value === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Should be more than 0',
          path: ['value'],
          fatal: true,
        });

        return z.NEVER;
      }
    }
  });

export const measureFilterSchema = z.object({
  filters: z.array(singleMeasureFilterSchema).min(1),
});
